// Action Center — derives a prioritized, real-data action feed for a Business
// Developer from the data CRE already has (leads, Upwork Jobs Intelligence,
// proposals, projects, performance overview). Pure logic, no React.

export type ActionCategory = "opportunity" | "followup" | "project" | "ai";
export type ActionPriority = "critical" | "important" | "info";
export type ActionKind =
  | "client-response"
  | "quote-request"
  | "lead-followup"
  | "bid-price-review"
  | "high-value"
  | "proposal-followup"
  | "project-review"
  | "ai-job"
  | "recommended-category";

export type ActionStatus = "ignored" | "completed" | "dismissed";

export interface ActionMetric {
  label: string;
  value: string;
}

export interface ActionLink {
  label: string;
  to?: string; // in-app route (react-router)
  href?: string; // external URL (opens new tab)
}

export interface ActionItem {
  id: string;
  kind: ActionKind;
  category: ActionCategory;
  priority: ActionPriority;
  title: string;
  subtitle: string;
  client?: string;
  metrics: ActionMetric[];
  timestamp: string | null;
  primary: ActionLink;
  secondary?: { label: string; status: ActionStatus };
  rank: number; // lower = higher in the feed
  recency: number; // ms epoch, tiebreaker
}

export interface ActionCenterContext {
  conversations: any[]; // chat contacts with unreadCount + lastMessageAt
  jobs: any[];
  proposals: any[];
  projects: any[];
  overview: any | null;
  leads?: any[]; // contacts/leads in the pipeline (for follow-up reminders)
  quoteRequests?: any[]; // service/quote requests assigned to this BD
  bids?: any[]; // submitted bids (for post-bid price-review reminders)
}

// A bid still "in play" — worth nudging the BD to revise its price to trigger a
// fresh Upwork notification. Once won/lost we stop reminding.
const BID_OPEN_STATUSES = ["Bid Submitted", "In Negotiation"];
const BID_REVIEW_DAYS = 2;

// Lead statuses that still need active follow-up (until Won/Lost).
const FOLLOWUP_STATUSES = ["Pending", "Need to Contact", "Contacted - No Response", "Negotiated"];
const FOLLOWUP_INTERVAL_DAYS = 2;

const HIGH_VALUE_BUDGET = 3000;
const CRITICAL_BUDGET = 5000;
const AI_SCORE_MIN = 70;
const PROPOSAL_FOLLOWUP_DAYS = 2;

const RANK: Record<ActionKind, number> = {
  "client-response": 1,
  "quote-request": 2,
  "lead-followup": 3,
  "bid-price-review": 4,
  "high-value": 5,
  "proposal-followup": 6,
  "project-review": 7,
  "ai-job": 8,
  "recommended-category": 9,
};

export const CATEGORY_OF: Record<ActionKind, ActionCategory> = {
  "client-response": "followup",
  "quote-request": "opportunity",
  "lead-followup": "followup",
  "bid-price-review": "followup",
  "high-value": "opportunity",
  "proposal-followup": "followup",
  "project-review": "project",
  "ai-job": "ai",
  "recommended-category": "ai",
};

const num = (v: any): number => {
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const money = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : `$${Math.round(n)}`;

const daysSince = (iso?: string | null): number => {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
};

// AI match score from a Jobs-Intelligence record. Handles 0-100 and 0-1 forms.
const jobScore = (job: any): number => {
  const raw = num(job?.ai?.["Weighted Scoring Score"]);
  if (raw > 0 && raw <= 1) return Math.round(raw * 100);
  return Math.round(raw);
};

const jobBudget = (job: any): number => num(job?.["Budget Amount"]);

const jobOpen = (job: any): boolean => {
  const s = String(job?.["Job Status"] || "").toLowerCase();
  return s === "" || s.includes("open") || s.includes("active");
};

const aiRecommended = (job: any): boolean => {
  const d = String(
    job?.ai?.["Final Decision"] || job?.ai?.["EV Calculator Decision"] || ""
  ).toLowerCase();
  return /recommend|yes|apply|go|pass|win|strong/.test(d);
};

const jobReason = (job: any): string =>
  String(
    job?.ai?.["Final Synthesis"] ||
      job?.ai?.["Weighted Scoring Reasoning"] ||
      ""
  ).slice(0, 140);

export function buildActionItems(ctx: ActionCenterContext): ActionItem[] {
  const items: ActionItem[] = [];
  const usedJobs = new Set<string>();

  // 1. Client Awaiting Response — real unread messages from a contact.
  for (const c of ctx.conversations || []) {
    const unread = Number(c.unreadCount) || 0;
    if (unread <= 0) continue;
    const ts = c.lastMessageAt || null;
    items.push({
      id: `conv-${c._id}`,
      kind: "client-response",
      category: CATEGORY_OF["client-response"],
      priority: "critical",
      title: "Client Awaiting Response",
      subtitle: `${unread} unread message${unread === 1 ? "" : "s"} — reply pending`,
      client: c.fullName || c.name || "Contact",
      metrics: [{ label: "Unread", value: String(unread) }],
      timestamp: ts,
      primary: { label: "Open Conversation", to: "/chat" },
      secondary: { label: "Ignore", status: "ignored" },
      rank: RANK["client-response"],
      recency: ts ? new Date(ts).getTime() : 0,
    });
  }

  // 1a. New Quote Requests — clients who submitted a Get-a-Quote brief.
  for (const qr of ctx.quoteRequests || []) {
    if (!["Submitted", "Under Review"].includes(String(qr.status))) continue;
    const clientName = qr.client?.name || "A client";
    const uname = qr.client?.username ? ` (@${qr.client.username})` : "";
    const ts = qr.createdAt || null;
    items.push({
      id: `qr-${qr._id}`,
      kind: "quote-request",
      category: CATEGORY_OF["quote-request"],
      priority: "important",
      title: "New Quote Request",
      subtitle: `${clientName}${uname} requested a quote for "${qr.service?.name || "a service"}"${qr.projectTitle ? ` — ${qr.projectTitle}` : ""}.`,
      client: clientName,
      metrics: [
        { label: "Ref", value: qr.requestNumber || "—" },
        { label: "Status", value: String(qr.status) },
      ],
      timestamp: ts,
      primary: { label: "Review Request", to: "/projects" },
      secondary: { label: "Ignore", status: "ignored" },
      rank: RANK["quote-request"],
      recency: ts ? new Date(ts).getTime() : 0,
    });
  }

  // 1b. Lead Follow-ups — recurring reminders for leads still in the pipeline.
  // Keep reminding (every FOLLOWUP_INTERVAL_DAYS of inactivity) until the lead
  // is marked Won or Lost. Updating the lead resets its timer.
  for (const lead of ctx.leads || []) {
    const status = String(lead.leadStatus || "");
    if (!FOLLOWUP_STATUSES.includes(status)) continue; // skips Won/Lost + others
    const lastTouch = lead.updatedAt || lead.createdAt || null;
    const stale = daysSince(lastTouch);
    if (stale < FOLLOWUP_INTERVAL_DAYS) continue; // followed up recently
    const who = lead.name || "Lead";
    items.push({
      id: `lead-${lead._id}`,
      kind: "lead-followup",
      category: CATEGORY_OF["lead-followup"],
      priority: stale >= 5 ? "critical" : "important",
      title: "Lead Follow-up Due",
      subtitle: `${who} is at "${status}" — ${stale} day${stale === 1 ? "" : "s"} without an update. Follow up to keep it moving.`,
      client: who,
      metrics: [
        { label: "Status", value: status },
        { label: "Idle", value: `${stale}d` },
      ],
      timestamp: lastTouch,
      primary: { label: "Open Lead", to: "/clients" },
      secondary: { label: "Ignore", status: "ignored" },
      rank: RANK["lead-followup"],
      recency: lastTouch ? new Date(lastTouch).getTime() : 0,
    });
  }

  // 2. High Value Opportunities (Jobs Intelligence, budget over threshold)
  const byBudget = [...(ctx.jobs || [])]
    .filter((j) => jobOpen(j) && jobBudget(j) >= HIGH_VALUE_BUDGET)
    .sort((a, b) => jobBudget(b) - jobBudget(a))
    .slice(0, 10);
  for (const j of byBudget) {
    usedJobs.add(j._id);
    const budget = jobBudget(j);
    const score = jobScore(j);
    const ts = j.createdAt || j["Published"] || null;
    items.push({
      id: `job-hv-${j._id}`,
      kind: "high-value",
      category: CATEGORY_OF["high-value"],
      priority: budget >= CRITICAL_BUDGET ? "critical" : "important",
      title: j["Job Title"] || "High Value Opportunity",
      subtitle: "High value opportunity matched from Jobs Intelligence",
      metrics: [
        { label: "Budget", value: money(budget) },
        ...(j["Category"] ? [{ label: "Category", value: j["Category"] }] : []),
        ...(score ? [{ label: "AI Match", value: `${score}%` }] : []),
      ],
      timestamp: ts,
      primary: j["URL"]
        ? { label: "View Job", href: j["URL"] }
        : { label: "View Job", to: "/upwork-jobs" },
      secondary: { label: "Ignore", status: "ignored" },
      rank: RANK["high-value"],
      recency: ts ? new Date(ts).getTime() : 0,
    });
  }

  // 3. Proposal Follow-Ups (sent N+ days ago)
  for (const p of ctx.proposals || []) {
    const d = daysSince(p.createdAt);
    if (d < PROPOSAL_FOLLOWUP_DAYS) continue;
    items.push({
      id: `proposal-${p._id}`,
      kind: "proposal-followup",
      category: CATEGORY_OF["proposal-followup"],
      priority: d >= 7 ? "critical" : "important",
      title: "Proposal Follow-Up Required",
      subtitle: `Submitted ${d} day${d === 1 ? "" : "s"} ago — no response yet`,
      client: p.brand || p.title || "—",
      metrics: [
        { label: "Days Since", value: String(d) },
        ...(p.category ? [{ label: "Category", value: p.category }] : []),
      ],
      timestamp: p.createdAt || null,
      primary: { label: "Open Proposal", to: "/proposals" },
      secondary: { label: "Mark Complete", status: "completed" },
      rank: RANK["proposal-followup"],
      recency: p.createdAt ? new Date(p.createdAt).getTime() : 0,
    });
  }

  // 3b. Post-Bid Price Review — nudge the BD to revise a still-open bid's price.
  // Updating the proposal price sends a fresh Upwork notification to the client,
  // which can re-open the job / re-engage them. Stops once the bid is won/lost.
  for (const b of ctx.bids || []) {
    if (!BID_OPEN_STATUSES.includes(String(b.bidStatus))) continue;
    const d = daysSince(b.createdAt);
    if (d < BID_REVIEW_DAYS) continue;
    const job = b.jobTitle || "Upwork job";
    items.push({
      id: `bid-price-${b._id}`,
      kind: "bid-price-review",
      category: CATEGORY_OF["bid-price-review"],
      priority: d >= 6 ? "critical" : "important",
      title: "Review & Update Proposal Price",
      subtitle: `No response in ${d} day${d === 1 ? "" : "s"} — consider lowering the price to re-notify the client on Upwork.`,
      client: job,
      metrics: [
        { label: "Days Since Bid", value: String(d) },
        ...(b.bidAmount ? [{ label: "Current Bid", value: money(num(b.bidAmount)) }] : []),
        ...(b.platformAccount ? [{ label: "Account", value: String(b.platformAccount) }] : []),
      ],
      timestamp: b.createdAt || null,
      primary: { label: "Open Bids", to: "/bids" },
      secondary: { label: "Mark Complete", status: "completed" },
      rank: RANK["bid-price-review"],
      recency: b.createdAt ? new Date(b.createdAt).getTime() : 0,
    });
  }

  // 4. Active Project Review (escrow funded / in progress needing attention)
  for (const p of ctx.projects || []) {
    const status = String(p.projectStatus || "");
    const escrow = num(p.escrowAmount);
    const needsReview =
      escrow > 0 && status !== "Completed" && status !== "Cancelled";
    if (!needsReview) continue;
    items.push({
      id: `project-${p._id}`,
      kind: "project-review",
      category: CATEGORY_OF["project-review"],
      priority: "important",
      title: "Project Review Required",
      subtitle: p.name || "Project needs your attention",
      client: typeof p.assignedContact === "object" ? p.assignedContact?.name : undefined,
      metrics: [
        { label: "Status", value: status || "—" },
        { label: "In Escrow", value: money(escrow) },
      ],
      timestamp: p.updatedAt || null,
      primary: { label: "Open Project", to: "/projects" },
      secondary: { label: "Mark Complete", status: "completed" },
      rank: RANK["project-review"],
      recency: p.updatedAt ? new Date(p.updatedAt).getTime() : 0,
    });
  }

  // 5. AI Job Recommendations (high match score, not already shown above)
  const byScore = [...(ctx.jobs || [])]
    .filter(
      (j) =>
        !usedJobs.has(j._id) &&
        jobOpen(j) &&
        jobScore(j) >= AI_SCORE_MIN &&
        aiRecommended(j)
    )
    .sort((a, b) => jobScore(b) - jobScore(a))
    .slice(0, 10);
  for (const j of byScore) {
    usedJobs.add(j._id);
    const score = jobScore(j);
    const budget = jobBudget(j);
    const reason = jobReason(j);
    const ts = j.createdAt || j["Published"] || null;
    items.push({
      id: `job-ai-${j._id}`,
      kind: "ai-job",
      category: CATEGORY_OF["ai-job"],
      priority: score >= 85 ? "important" : "info",
      title: j["Job Title"] || "AI Job Recommendation",
      subtitle: reason || "Strong AI match for your profile",
      metrics: [
        { label: "AI Match", value: `${score}%` },
        ...(budget ? [{ label: "Budget", value: money(budget) }] : []),
        ...(j["Category"] ? [{ label: "Category", value: j["Category"] }] : []),
      ],
      timestamp: ts,
      primary: j["URL"]
        ? { label: "View Job", href: j["URL"] }
        : { label: "View Job", to: "/upwork-jobs" },
      secondary: { label: "Ignore", status: "ignored" },
      rank: RANK["ai-job"],
      recency: ts ? new Date(ts).getTime() : 0,
    });
  }

  // 6. Recommended Category Today (top project category + matching jobs)
  const cat = topCategory(ctx.projects || []);
  if (cat) {
    const winRate = Math.round(num(ctx.overview?.totals?.winRate));
    const matching = (ctx.jobs || []).filter((j) =>
      String(j["Category"] || "")
        .toLowerCase()
        .includes(cat.toLowerCase())
    ).length;
    items.push({
      id: `category-${cat}`,
      kind: "recommended-category",
      category: CATEGORY_OF["recommended-category"],
      priority: "info",
      title: "Recommended Category Today",
      subtitle: cat,
      metrics: [
        { label: "Win Rate", value: `${winRate}%` },
        { label: "Matching Jobs", value: String(matching) },
      ],
      timestamp: null,
      primary: { label: "View Opportunities", to: "/upwork-jobs" },
      secondary: { label: "Dismiss", status: "dismissed" },
      rank: RANK["recommended-category"],
      recency: 0,
    });
  }

  items.sort((a, b) => a.rank - b.rank || b.recency - a.recency);
  return items;
}

function topCategory(projects: any[]): string | null {
  const counts: Record<string, number> = {};
  for (const p of projects) {
    const t = p.projectType;
    if (!t) continue;
    counts[t] = (counts[t] || 0) + 1;
  }
  let best: string | null = null;
  let max = 0;
  for (const [k, v] of Object.entries(counts)) {
    if (v > max) {
      max = v;
      best = k;
    }
  }
  return best;
}

export const CATEGORY_TABS: { key: ActionCategory | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "opportunity", label: "Opportunities" },
  { key: "followup", label: "Follow-Ups" },
  { key: "project", label: "Projects" },
  { key: "ai", label: "AI Recommendations" },
];

// Three top-level workspace groups for the redesigned Action Center. Every kind
// maps to exactly one group so items land in the right tab.
export type ActionGroup = "notifications" | "followups" | "jobs";

export const GROUP_OF: Record<ActionKind, ActionGroup> = {
  "client-response": "notifications",
  "quote-request": "notifications",
  "project-review": "notifications",
  "lead-followup": "followups",
  "proposal-followup": "followups",
  "bid-price-review": "followups",
  "high-value": "jobs",
  "ai-job": "jobs",
  "recommended-category": "jobs",
};

export const ACTION_GROUP_TABS: { key: ActionGroup; label: string }[] = [
  { key: "notifications", label: "Notifications & Requests" },
  { key: "followups", label: "Follow-ups" },
  { key: "jobs", label: "Job Opportunities" },
];

export const groupOf = (item: ActionItem): ActionGroup => GROUP_OF[item.kind];
